import { create, StoreApi } from "zustand"
import { subMonths, subWeeks } from "date-fns"
import { fetchRevenueHistoryByRestaurant, fetchRevenueHistoryForAllRestaurants, fetchRevenueHistoryByRevenueSourceIds, fetchRevenueHistoryDateBounds, RevenueHistory } from "../services/revenuehistory/revenueHistoryService"
import { ALL_RESTAURANTS_ID } from "./appUserDefaultStore"
import { FrontendRestaurantRevenueSource } from "../services/restaurant/restaurantRevenueSourceService"

// Progressive loading: the first fetch covers only the currently-viewed period
// (defaulting to the last week, matching the default time selector) for the fastest
// possible first render. Everything older then streams in month-by-month in the
// background. When the user switches to an older/larger period, that exact window is
// fetched in one shot ahead of the background crawl (see desiredFloor / the pump below).
const DEFAULT_INITIAL_WINDOW_WEEKS = 1;
const CHUNK_MONTHS = 1;
// Hard cap on the span any single pump request may cover. The desiredFloor gap fetch is
// otherwise unbounded (ChartsPage sets desiredFloor to the beginning of time, asking for
// the client's entire remaining history in one request) — huge payloads are the most
// likely trigger of a 500 from getByRevenueSourceIds, so we slice the gap into cap-sized
// requests walking backwards instead.
const MAX_FETCH_WINDOW_MONTHS = 6;
// Delay before retrying a failed background-pump chunk fetch once (network blip,
// laptop sleep, token refresh) before giving up on that pump run.
const PUMP_RETRY_DELAY_MS = 2000;

// Module-level coordination so a non-progressive caller (e.g. another page) can
// piggy-back on an in-flight progressive load instead of refetching, and so a
// source switch can cancel an in-flight background loop.
let activeLoadKey: string | null = null;
let activeLoadPromise: Promise<void> | null = null;
let loadGeneration = 0;
// The inputs needed to RESUME a background pump that died or never finished (see
// resumeRevenuePump). Captured when a progressive load starts; cleared when the store is
// cleared for a restaurant.
let revenueResumeContext: { revenueSourceIds: string[]; revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]> } | null = null;

const sourceKey = (revenueSourceIds: string[]) => [...revenueSourceIds].sort().join(',');

/** Enrich revenue entries with their revenueSourceType from the sources map. */
export function enrichRevenue(
  data: RevenueHistory[],
  revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>
): RevenueHistory[] {
  if (!revenueSourcesMap) return data;
  // Build a single lookup: revenueSourceId → { restaurantId, revenueSourceType }.
  // Resolving the restaurantId here (the same way `groupRevenueByRestaurant` does) is
  // what keeps the restaurant filter consistent with the per-restaurant grouping —
  // otherwise records loaded by revenue-source IDs have no `restaurantId` and the
  // restaurant filter (which matches on `restaurantId`) would drop everything → €0.
  const sourceLookup = new Map<string, { restaurantId: string; revenueSourceType?: FrontendRestaurantRevenueSource['revenueSourceType'] }>();
  for (const [restaurantId, sources] of Object.entries(revenueSourcesMap)) {
    for (const source of sources) {
      sourceLookup.set(source.id, { restaurantId, revenueSourceType: source.revenueSourceType });
    }
  }
  return data.map(rev => {
    if (!rev.restaurantRevenueSourceId) return rev;
    const info = sourceLookup.get(rev.restaurantRevenueSourceId);
    if (!info) return rev;
    return {
      ...rev,
      revenueSourceType: info.revenueSourceType,
      restaurantId: rev.restaurantId || info.restaurantId,
    };
  });
}

/** Merge new revenue into an existing list, de-duplicating by id (last write wins). */
function mergeRevenueDedup(existing: RevenueHistory[], incoming: RevenueHistory[]): RevenueHistory[] {
  const map = new Map(existing.map(r => [r.id, r]));
  for (const r of incoming) map.set(r.id, r);
  return Array.from(map.values());
}

type RevenueSet = StoreApi<RevenueHistoryState>['setState'];
type RevenueGet = StoreApi<RevenueHistoryState>['getState'];

/**
 * Background pump: crawl older revenue from the current loadedFromDate down to minDate
 * (the earliest revenue date), fetching the gap to desiredFloor in one shot when the user
 * has selected a further-back period, otherwise one month at a time. Marks the store
 * fullyLoaded when it reaches minDate. Shared by the initial progressive load and the
 * resume path so a pump that died mid-crawl can restart without refetching loaded data.
 * A failed chunk fetch is retried once after a short delay before giving up; on giving
 * up the store stays at !fullyLoaded with its loaded list intact so ensureViewLoaded can
 * resume it. Honours the generation/stale mechanism via isStale.
 */
async function pumpOlderRevenue(
  set: RevenueSet,
  get: RevenueGet,
  revenueSourceIds: string[],
  revenueSourcesMap: Record<string, FrontendRestaurantRevenueSource[]> | undefined,
  minDate: Date,
  isStale: () => boolean,
): Promise<void> {
  while (true) {
    if (isStale()) return;
    const cursorEnd = get().loadedFromDate;
    if (!cursorEnd || cursorEnd <= minDate) break;

    const desired = get().desiredFloor;
    let cursorStart: Date;
    if (desired && desired < cursorEnd) {
      // Fetch toward the desired floor, but never span more than MAX_FETCH_WINDOW_MONTHS
      // in one request — when the floor is further back than the cap, take a cap-sized
      // slice (nearest first) and let the loop walk the rest, so a far-back floor never
      // becomes a single huge request.
      const cappedStart = subMonths(cursorEnd, MAX_FETCH_WINDOW_MONTHS);
      const target = desired < cappedStart ? cappedStart : desired;
      cursorStart = target < minDate ? minDate : target;
    } else {
      const candidateStart = subMonths(cursorEnd, CHUNK_MONTHS);
      cursorStart = candidateStart < minDate ? minDate : candidateStart;
    }

    let chunk: RevenueHistory[];
    try {
      chunk = await fetchRevenueHistoryByRevenueSourceIds(revenueSourceIds, cursorStart, cursorEnd);
    } catch {
      // Retry once after a short delay before giving up, so a transient failure doesn't
      // strand the store at !fullyLoaded forever (ensureViewLoaded can resume later).
      if (isStale()) return;
      await new Promise(r => setTimeout(r, PUMP_RETRY_DELAY_MS));
      if (isStale()) return;
      try {
        chunk = await fetchRevenueHistoryByRevenueSourceIds(revenueSourceIds, cursorStart, cursorEnd);
      } catch (retryErr) {
        console.error('[ERROR] progressive revenue history pump chunk failed after retry', retryErr);
        if (!isStale()) set({ isBackgroundLoading: false });
        return;
      }
    }
    if (isStale()) return;
    set(s => ({
      revenueHistoryList: mergeRevenueDedup(s.revenueHistoryList, enrichRevenue(chunk, revenueSourcesMap)),
      loadedFromDate: cursorStart,
    }));
  }

  if (isStale()) return;
  set({ fullyLoaded: true, isBackgroundLoading: false, loadedFromDate: minDate });
}

/**
 * Resume a background pump that stopped before loading the full history (it gave up after
 * a failed fetch, or its process was interrupted). Re-derives the date bounds and restarts
 * the crawl from the current loadedFromDate WITHOUT clearing the loaded list. No-op if no
 * resume context was captured. Called by ensureViewLoaded when there's loaded data but not
 * the full history and no load is currently running.
 */
function resumeRevenuePump(set: RevenueSet, get: RevenueGet): void {
  const ctx = revenueResumeContext;
  if (!ctx) return;
  const key = sourceKey(ctx.revenueSourceIds);
  const myGeneration = ++loadGeneration;
  const isStale = () => loadGeneration !== myGeneration;
  activeLoadKey = key;

  const run = (async () => {
    set({ isBackgroundLoading: true });
    try {
      const { minDate } = await fetchRevenueHistoryDateBounds(ctx.revenueSourceIds).catch(() => ({ minDate: null, maxDate: null }));
      if (isStale()) return;
      // Can't determine the floor — stay resumable rather than looping unbounded.
      if (!minDate) { set({ isBackgroundLoading: false }); return; }
      // pumpOlderRevenue handles the already-covered case (breaks immediately → fullyLoaded).
      await pumpOlderRevenue(set, get, ctx.revenueSourceIds, ctx.revenueSourcesMap, minDate, isStale);
    } catch (err) {
      console.error('[ERROR] resuming progressive revenue history pump', err);
      if (!isStale()) set({ isBackgroundLoading: false });
    } finally {
      if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
    }
  })();
  activeLoadPromise = run;
}

interface RevenueHistoryState {
  revenueHistoryList: RevenueHistory[];
  currentRestaurantId: string | null;
  currentRevenueSourceIds: string[] | null;
  isLoading: boolean;
  // Progressive-loading state (used by the dashboard for early render). Mirrors the
  // order-history store: firstChunkLoaded = initial window rendered; fullyLoaded =
  // everything back to the earliest date is present; loadedFromDate = earliest date
  // currently covered (null = from the beginning of time); isBackgroundLoading =
  // older chunks still streaming.
  firstChunkLoaded: boolean;
  fullyLoaded: boolean;
  loadedFromDate: Date | null;
  isBackgroundLoading: boolean;
  // The oldest date the user currently wants visible. The background pump fetches the
  // gap down to this date in a single request (ahead of its month-by-month crawl) so the
  // selected period shows as fast as possible.
  desiredFloor: Date | null;
  load: (restaurantId: string, revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>) => Promise<void>
  loadByRevenueSourceIds: (restaurantId: string, revenueSourceIds: string[], revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>) => Promise<void>
  loadByRevenueSourceIdsProgressive: (restaurantId: string, revenueSourceIds: string[], revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>, viewStart?: Date) => Promise<void>
  // Called when the viewed period changes: prioritises fetching back to rangeStart.
  ensureViewLoaded: (rangeStart: Date) => void
  byId: (id: string) => RevenueHistory | undefined;
  isDataLoadedFor: (restaurantId: string) => boolean;
  isDataLoadedForRevenueSources: (revenueSourceIds: string[]) => boolean;
  isRangeLoaded: (rangeStart: Date) => boolean;

  clearRevenuehistoryDataForRestaurant: (restaurantId: string) => void;
  markRevenuehistoryEmptyForRestaurant: (restaurantId: string) => void;

  userStoredDefaults: Record<string, any>;
  setUserStoredDefaults: (defaults: Record<string, any>) => void;
}

export const useRevenueHistory = create<RevenueHistoryState>((set, get) => ({
  revenueHistoryList: [],
  currentRestaurantId: null,
  currentRevenueSourceIds: null,
  isLoading: false,
  firstChunkLoaded: false,
  fullyLoaded: false,
  loadedFromDate: null,
  isBackgroundLoading: false,
  desiredFloor: null,

  userStoredDefaults: {
    rangeType: 'week',
    currentStart: new Date(),
    dropdownOpen: false,
  },
  setUserStoredDefaults: defaults =>
    set(state => ({
      userStoredDefaults: { ...state.userStoredDefaults, ...defaults }
  })),

  load: async (restaurantId: string, revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>) => {
    // Skip if data is already loaded for this restaurant
    if (get().isDataLoadedFor(restaurantId)) {
      return;
    }

    set({ isLoading: true });

    try {
      let data: RevenueHistory[];

      if (restaurantId === ALL_RESTAURANTS_ID) {
        data = await fetchRevenueHistoryForAllRestaurants();
      } else {
        data = await fetchRevenueHistoryByRestaurant(restaurantId);
      }

      const enriched = enrichRevenue(data, revenueSourcesMap);

      set({
        revenueHistoryList: enriched,
        currentRestaurantId: restaurantId,
        currentRevenueSourceIds: null,
        isLoading: false,
        firstChunkLoaded: true,
        fullyLoaded: true,
        loadedFromDate: null,
        isBackgroundLoading: false,
      });
    } catch (err) {
      console.error('[ERROR] loading revenue history', err);
      set({ isLoading: false });
    }
  },

  loadByRevenueSourceIds: async (restaurantId: string, revenueSourceIds: string[], revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>) => {
    // Skip if data is already fully loaded for these revenue source IDs
    if (get().isDataLoadedForRevenueSources(revenueSourceIds)) {
      return;
    }

    // If a progressive (or other) load for these exact sources is already in flight,
    // piggy-back on it instead of firing a second full fetch.
    const key = sourceKey(revenueSourceIds);
    if (activeLoadKey === key && activeLoadPromise) {
      set({ isLoading: true });
      try {
        await activeLoadPromise;
      } catch { /* errors already logged by the in-flight load */ }
      // The awaited progressive/full load signals completion via fullyLoaded, not
      // isLoading, so our `isLoading: true` above would otherwise stick forever. That
      // includes the pump-give-up case (run resolves with fullyLoaded still false), so
      // key off "no load is running anymore" rather than fullyLoaded — if a newer load
      // superseded the awaited one (e.g. a restaurant switch), activeLoadPromise is
      // non-null and that load manages its own isLoading.
      if (activeLoadPromise === null) set({ isLoading: false });
      return;
    }

    const myGeneration = ++loadGeneration;
    activeLoadKey = key;
    set({ isLoading: true });

    const run = (async () => {
      try {
        const data = await fetchRevenueHistoryByRevenueSourceIds(revenueSourceIds);
        if (loadGeneration !== myGeneration) return; // superseded by a newer load

        const enriched = enrichRevenue(data, revenueSourcesMap);

        set({
          revenueHistoryList: enriched,
          currentRevenueSourceIds: revenueSourceIds,
          currentRestaurantId: restaurantId,
          isLoading: false,
          firstChunkLoaded: true,
          fullyLoaded: true,
          loadedFromDate: null,
          isBackgroundLoading: false,
        });
      } catch (err) {
        console.error('[ERROR] loading revenue history by revenue source IDs', err);
        set({ isLoading: false });
      } finally {
        if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
      }
    })();
    activeLoadPromise = run;
    await run;
  },

  loadByRevenueSourceIdsProgressive: async (restaurantId: string, revenueSourceIds: string[], revenueSourcesMap?: Record<string, FrontendRestaurantRevenueSource[]>, viewStart?: Date) => {
    // Skip if already fully loaded for these sources.
    if (get().isDataLoadedForRevenueSources(revenueSourceIds)) {
      return;
    }

    const key = sourceKey(revenueSourceIds);
    // A load for these sources is already running — the dashboard reads store state
    // reactively, so there's nothing to await; just don't start a second one.
    if (activeLoadKey === key && activeLoadPromise) {
      return;
    }

    const myGeneration = ++loadGeneration;
    const isStale = () => loadGeneration !== myGeneration;
    activeLoadKey = key;
    // Capture what a later resume needs to restart the pump (see resumeRevenuePump).
    revenueResumeContext = { revenueSourceIds, revenueSourcesMap };

    const now = new Date();
    // The initial fetch covers the currently-viewed period, extended one week further
    // back so a freshly-started week (whose window is nearly empty) still shows the
    // previous week without waiting for a follow-up fetch. Falls back to the default
    // window when no viewStart is given.
    const initialStart = viewStart ? subWeeks(viewStart, 1) : subWeeks(now, DEFAULT_INITIAL_WINDOW_WEEKS);

    const run = (async () => {
      set({
        isLoading: true,
        isBackgroundLoading: true,
        firstChunkLoaded: false,
        fullyLoaded: false,
        loadedFromDate: null,
        desiredFloor: initialStart,
        revenueHistoryList: [],
        currentRevenueSourceIds: revenueSourceIds,
        currentRestaurantId: restaurantId,
      });

      try {
        // Fetch the earliest revenue date in parallel so we know how far to chunk.
        const boundsPromise = fetchRevenueHistoryDateBounds(revenueSourceIds).catch(() => ({ minDate: null, maxDate: null }));

        // 1) Initial window — render as soon as this arrives.
        const initialData = await fetchRevenueHistoryByRevenueSourceIds(revenueSourceIds, initialStart, now);
        if (isStale()) return;
        set({
          revenueHistoryList: enrichRevenue(initialData, revenueSourcesMap),
          loadedFromDate: initialStart,
          firstChunkLoaded: true,
          isLoading: false,
        });

        const { minDate } = await boundsPromise;
        if (isStale()) return;

        // Nothing older than the initial window — we're done.
        if (!minDate || minDate >= initialStart) {
          set({ fullyLoaded: true, isBackgroundLoading: false, loadedFromDate: minDate ?? initialStart });
          return;
        }

        // 2) Background pump: keep loading older revenue until the earliest date (marks
        // fullyLoaded on completion). See pumpOlderRevenue for the crawl/gap logic.
        await pumpOlderRevenue(set, get, revenueSourceIds, revenueSourcesMap, minDate, isStale);
      } catch (err) {
        console.error('[ERROR] progressive revenue history load', err);
        if (!isStale()) set({ isLoading: false, isBackgroundLoading: false });
      } finally {
        if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
      }
    })();
    activeLoadPromise = run;
    await run;
  },

  ensureViewLoaded: (rangeStart: Date) => {
    const state = get();
    // Everything is already in the store, or the requested period is already covered.
    if (state.fullyLoaded) return;
    if (state.loadedFromDate && rangeStart >= state.loadedFromDate) return;
    // Only ever push the floor older — the running background pump picks this up and
    // fetches the gap in one request before resuming its month-by-month crawl.
    if (!state.desiredFloor || rangeStart < state.desiredFloor) {
      set({ desiredFloor: rangeStart });
    }
    // If the initial window has landed but the pump is no longer running (it died after
    // a failed fetch, or was interrupted), restart it — otherwise desiredFloor would be
    // set with nothing reading it and this period would spin forever.
    if (state.firstChunkLoaded && activeLoadPromise === null) {
      resumeRevenuePump(set, get);
    }
  },

  byId: id => get().revenueHistoryList.find(r => r.id === id),

  isDataLoadedFor: (restaurantId: string) => get().currentRestaurantId === restaurantId,

  isDataLoadedForRevenueSources: (revenueSourceIds: string[]) => {
    const state = get();
    // Only treat as "loaded" once the full history is present, so non-progressive
    // consumers (charts, reports, revenue pages) never read a partial dataset.
    if (!state.fullyLoaded) return false;
    const current = state.currentRevenueSourceIds;
    if (!current || current.length !== revenueSourceIds.length) return false;
    const sortedCurrent = [...current].sort();
    const sortedNew = [...revenueSourceIds].sort();
    return sortedCurrent.every((id, index) => id === sortedNew[index]);
  },

  isRangeLoaded: (rangeStart: Date) => {
    const state = get();
    if (state.fullyLoaded) return true;
    if (!state.firstChunkLoaded || !state.loadedFromDate) return false;
    // The requested period is covered iff it starts at/after the earliest loaded date.
    return rangeStart >= state.loadedFromDate;
  },

  clearRevenuehistoryDataForRestaurant: (restaurantId: string) => {
    loadGeneration++; // cancel any in-flight background load
    activeLoadKey = null;
    activeLoadPromise = null;
    revenueResumeContext = null; // don't resume a pump for the cleared restaurant
    set({
      revenueHistoryList: [],
      currentRevenueSourceIds: [],
      currentRestaurantId: null,
      firstChunkLoaded: false,
      fullyLoaded: false,
      loadedFromDate: null,
      isBackgroundLoading: false,
      desiredFloor: null,
    });
  },

  // Restaurant has no revenue sources configured: there is nothing to fetch, so mark
  // the store as fully loaded with an empty list. Without this, consumers that gate on
  // firstChunkLoaded/fullyLoaded (e.g. the dashboard) would spin forever instead of
  // showing 0.
  markRevenuehistoryEmptyForRestaurant: (restaurantId: string) => {
    loadGeneration++; // cancel any in-flight background load
    activeLoadKey = null;
    activeLoadPromise = null;
    revenueResumeContext = null; // don't resume a pump for the cleared restaurant
    set({
      revenueHistoryList: [],
      currentRevenueSourceIds: [],
      currentRestaurantId: restaurantId,
      isLoading: false,
      firstChunkLoaded: true,
      fullyLoaded: true,
      loadedFromDate: null,
      isBackgroundLoading: false,
      desiredFloor: null,
    });
  }
}));